import {memo, useCallback} from 'react';
import {decimalFormat, translate} from '../../utils';
import {apollo} from '../../graphql';
import {gql} from '@apollo/client';
import {clientSearch, editClientModal, editPersonModal} from '../../modals';
import {startLoadingIndicator, stopLoadingIndicator} from '../../loading';
import {flushSync} from 'react-dom';
import {ItemsTable} from './ItemsTable';
import {ItemsSorter} from './ItemsSorter';
import {InvoiceDraftPaymentMethods} from './InvoiceDraftPaymentMethods';
import {useSubmitHandler} from '../../hooks/useSubmitHandler';
import {EInvoiceStatus} from './EInvoiceStatus';

const INVOICE_ITEMS_LIMIT = 250;

export const InvoiceDraftPanel = memo(({invoiceDraft, setInvoiceDraft, reservationKey, paymentMethods, remove, alreadyInvoicedItems, addItems, removeItems, toggleSelection, previewAction, finalizeAction, sendAction}) => {
    const collection = invoiceDraft.collection;
    const isInvoiced = invoiceDraft.invoice !== null;
    const paymentType = invoiceDraft.paymentMethodAmounts.every(paymentMethodAmount => paymentMethodAmount.amount === null) ? 'single' : 'split';

    const setPaymentMethodAmounts = useCallback(paymentMethodAmounts => {
        setInvoiceDraft(invoiceDraft, {paymentMethodAmounts});
    }, [invoiceDraft, setInvoiceDraft]);

    const [isSubmitting, submitHandler] = useSubmitHandler(async (event) => {
        const errors = validatePaymentMethodAmounts(invoiceDraft.paymentMethodAmounts, collection.total);
        setInvoiceDraft(invoiceDraft, {
            ...invoiceDraft,
            paymentMethodAmounts: invoiceDraft.paymentMethodAmounts.map((paymentMethodAmount, i) => {
                if (errors[i] === null) {
                    return {
                        paymentMethod: paymentMethodAmount.paymentMethod,
                        amount: paymentMethodAmount.amount,
                    };
                } else {
                    return {
                        ...paymentMethodAmount,
                        error: errors[i],
                    };
                }
            }),
        });
        if (errors.some(error => error !== null)) {
            return;
        }

        const action = event.nativeEvent.submitter.name;
        if (action === 'preview') {
            await previewAction(invoiceDraft);
        } else if (action === 'finalize') {
            await finalizeAction(invoiceDraft);
        } else if (action === 'send') {
            await sendAction(invoiceDraft);
        }
    });

    const toggleItemSelection = useCallback((collection, item) => {
        if (!isInvoiced) {
            toggleSelection(collection, item);
        }
    }, [isInvoiced, toggleSelection]);

    function clientSearchAction() {
        clientSearch(
            ({key}) => {
                startLoadingIndicator();
                loadClient(key).then(response => {
                    // Ensured that when loading spinner stops (which is not controlled by react),
                    // DOM has already been updated with the new reservation.
                    flushSync(() => {
                        setInvoiceDraft(invoiceDraft, {recipient: [response.data.clients.edges[0].node, null]});
                    });
                }).finally(() => {
                    stopLoadingIndicator();
                });
            },
            { reservationKey }
        );
    }

    function clientEditAction() {
        editCreateClient(invoiceDraft.recipient[0], invoiceDraft.recipient[1]).then(recipient => {
            setInvoiceDraft(invoiceDraft, {recipient});
        });
    }

    function clientNewAction() {
        editCreateClient(null, null).then(recipient => {
            setInvoiceDraft(invoiceDraft, {recipient});
        });
    }

    function togglePaymentType() {
        const nextPaymentType = paymentType === 'single' ? 'split' : 'single';
        setPaymentMethodAmounts([
            {
                paymentMethod: invoiceDraft.paymentMethodAmounts[0].paymentMethod,
                amount: nextPaymentType === 'split' ? collection.total : null,
            },
            {paymentMethod: null, amount: nextPaymentType === 'split' ? 0.0 : null},
            {paymentMethod: null, amount: nextPaymentType === 'split' ? 0.0 : null},
        ]);
    }

    const alerts = [...invoiceDraft.alerts];
    if (collection.items.some(([type, item]) => alreadyInvoicedItems[type + 's'].has(item.id))) {
        alerts.push(translate('frontend', 'ITEMS_ALREADY_INVOICED_ERROR'));
    }
    if (collection.items.length > INVOICE_ITEMS_LIMIT) {
        alerts.push(translate('frontend', 'SPLIT_INVOICE_ITEMS_LIMIT_REACHED_ALERT', {
            limit: INVOICE_ITEMS_LIMIT,
            overLimit: collection.items.length - INVOICE_ITEMS_LIMIT,
        }));
    }
    if (paymentType === 'split' && collection.total !== parseFloat(invoiceDraft.paymentMethodAmounts.reduce((carry, paymentMethodAmount) => carry + paymentMethodAmount.amount, 0.0).toFixed(2))) {
        alerts.push(translate('frontend', 'The total of all methods of payment does not match the invoice total'));
    }

    return <section className={`row ${isInvoiced ? 'disabled' : ''}`}>
        <div className="col-xs-10 col-xs-offset-1">
            <div className="btn_rightscroll left_ab">
                <ItemsSorter addItems={addItems && !isInvoiced ? (predicate) => addItems(invoiceDraft, predicate) : null} removeItems={removeItems && !isInvoiced ? (predicate) => removeItems(invoiceDraft, predicate) : null}/>
            </div>
            {isInvoiced ? <>&nbsp;</> : <div className="hstack gap-1 justify-end mb-1">
                <button type="button" className="btn btn-default-clear btn-sm" onClick={clientEditAction}>
                    {translate('frontend', 'EDIT')}
                </button>
                <button type="button" className="btn btn-default-clear btn-sm" onClick={clientSearchAction}>
                    {translate('frontend', 'Search')}
                </button>
                <button type="button" className="btn btn-default-clear btn-sm" onClick={clientNewAction}>
                    {translate('frontend', 'NEW')}
                </button>
            </div>}
            <div className="box box-solid mb-1">
                <div className="box-header items-center">
                    <h3 className="box-title">
                        <span className="hstack gap-1">
                            <EInvoiceStatus eInvoice={invoiceDraft.eInvoice}/>
                            <span>
                                {translate('frontend', 'Recipient')}: <span className="font-size-14 word-break">{clientToString(invoiceDraft.recipient[0])} {invoiceDraft.recipient[1] ? `(${clientToString(invoiceDraft.recipient[1])})` : ''}</span>
                            </span>
                        </span>
                    </h3>

                    <div className="box-tools">
                        {isInvoiced ? <span className="invoiced-items-header">
                            <i className="fa fa-check"/> {translate('frontend', 'invoiced')}
                        </span> :
                            <button type="button" className="btn btn-default-clear" onClick={() => remove(invoiceDraft)}>
                                <i className="fa fa-xmark"/>
                            </button>}
                    </div>
                </div>

                <div className="box-body">
                    {alerts.map((text, i) => (
                        <div key={i} className="alert alert-danger alert-small">{text}</div>
                    ))}
                    <ItemsTable collection={collection} alreadyInvoicedItems={alreadyInvoicedItems} toggleSelection={toggleItemSelection}/>

                    {collection.items.length === 0 || collection.total !== 0.0 ?
                        <fieldset className="fieldset-native mt-3" disabled={isInvoiced}>
                            <InvoiceDraftPaymentMethods paymentMethods={paymentMethods} paymentMethodAmounts={invoiceDraft.paymentMethodAmounts} setPaymentMethodAmounts={setPaymentMethodAmounts} totalAmount={collection.total}/>
                        </fieldset> : null}
                </div>
            </div>
            {collection.total > 0.0 && !isInvoiced ? <div className="text-right">
                <button type="button" className="btn btn-default-clear btn-sm split-payment" onClick={togglePaymentType}>
                    {paymentType === 'single' ? translate('frontend', 'Split payment') : translate('frontend', 'Cancel split payment')}
                </button>
            </div> : null}
            <form onSubmit={submitHandler}>
                <fieldset className="fieldset-native" disabled={isSubmitting || alerts.length > 0 || collection.items.length === 0}>
                    <div className="btn-group-vertical btn_rightscroll right_ab">
                        <button type="submit" name="preview" className="btn btn-default-clear" disabled={isInvoiced}>
                            <i className="fa fa-eye"/>
                        </button>

                        <button type="submit" name="finalize" className="btn btn-default-clear">
                            <i className="fa fa-print"/>
                        </button>

                        <button type="submit" name="send" className="btn btn-default-clear">
                            <i className="fa fa-envelope"/>
                        </button>
                    </div>
                </fieldset>
            </form>
        </div>
    </section>;
});
InvoiceDraftPanel.displayName = 'InvoiceDraftPanel';

function clientToString(client) {
    if (client.company) {
        return client.company;
    } else {
        return `${client.clientTitle?.name || ''} ${client.firstname} ${client.lastname}`.trim().replace(/\s{2,}/g, ' ');
    }
}

function loadClient(clientKey) {
    return apollo.query({
        query: gql`
            query InvoiceClient($client:ID!) {
                clients(filter:{id:{eq:$client}} first:1) {
                    edges {
                        node {
                            id
                            ...on Person {
                                clientTitle { name }
                                firstname
                                lastname
                            }
                            ...on Company {
                                company
                            }
                        }
                    }
                }
            }
        `,
        variables: {
            client: clientKey,
        },
    });
}

function editCreateClient(client, contact) {
    return new Promise((resolve) => {
        editClientModal(
            client?.id,
            null,
            (client, contact) => {
                const nextRecipient = [null, null];
                if (client.client_company) {
                    nextRecipient[0] = {
                        id: client.client_key,
                        company: client.client_company,
                    };
                } else {
                    nextRecipient[0] = {
                        id: client.client_key,
                        firstname: client.client_firstname,
                        lastname: client.client_lastname,
                    };
                    if (client.client_title) {
                        nextRecipient[0].clientTitle = {name: client.client_title};
                    }
                }

                if (contact) {
                    nextRecipient[1] = {
                        id: contact.client_key,
                        firstname: contact.client_firstname,
                        lastname: contact.client_lastname,
                    };
                    if (contact.client_title) {
                        nextRecipient[1].clientTitle = {name: contact.client_title};
                    }
                }

                resolve(nextRecipient);
            },
            contact?.id,
            (clientKey, contactKey) => {
                return new Promise((resolve) => {
                    editPersonModal(
                        'short',
                        contactKey,
                        clientKey,
                        'client-split-invoice-screen',
                        undefined,
                        undefined,
                        'PERSON',
                        undefined,
                        (contactPerson) => {
                            // Tells client modal to update the contact HTML
                            resolve(contactPerson);

                            // Tells person modal to close
                            return Promise.resolve();
                        },
                    );
                });
            },
        );
    });
}

/**
 * @param {Number} totalAmount
 * @return {?String[]} List of errors
 */
function validatePaymentMethodAmounts(paymentMethodAmounts, totalAmount) {
    const paymentType = paymentMethodAmounts.every(paymentMethodAmount => paymentMethodAmount.amount === null) ? 'single' : 'split';

    if (paymentType === 'single') {
        if (totalAmount !== 0.0) {
            if (paymentMethodAmounts[0].paymentMethod === null) {
                return [
                    translate('frontend', 'You have not entered the first payment method'),
                    null,
                    null,
                ];
            } else if (window.config.stripe.reader_payment_method && window.config.stripe.reader_payment_method === paymentMethodAmounts[0].paymentMethod && totalAmount < window.config.stripe.minimum_amount) {
                return [
                    translate('yii', '{attribute} must be no less than {min}.', {
                        attribute: translate('frontend', 'AMOUNT'),
                        min: decimalFormat(window.config.stripe.minimum_amount, 2),
                    }),
                    null,
                    null,
                ];
            }
        }

        return [null, null, null];
    } else {
        return paymentMethodAmounts.map((paymentMethodAmount, i) => {
            if (paymentMethodAmount.amount !== 0.0 && !paymentMethodAmount.paymentMethod) {
                if (i === 0) {
                    return translate('frontend', 'You have not entered the first payment method');
                } else if (i === 1) {
                    return translate('frontend', 'You have not entered the second payment method');
                } else {
                    return translate('frontend', 'You have not entered the third payment method');
                }
            } else if (window.config.stripe.reader_payment_method && window.config.stripe.reader_payment_method === paymentMethodAmount.paymentMethod && paymentMethodAmount.amount < window.config.stripe.minimum_amount) {
                return translate('yii', '{attribute} must be no less than {min}.', {
                    attribute: translate('frontend', 'AMOUNT'),
                    min: decimalFormat(window.config.stripe.minimum_amount, 2),
                });
            } else if (paymentMethodAmount.amount < 0.0) {
                return translate('yii', '{attribute} must be no less than {min}.', {
                    attribute: translate('frontend', 'AMOUNT'),
                    min: decimalFormat(0.01, 2),
                });
            }

            return null;
        });
    }
}
